/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import Modal from 'antd/lib/modal/Modal';
import { ModalProps } from 'antd/lib/modal';
import { useIntl } from 'react-intl';
import { Col, Row, Spin } from 'antd';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { DbFolder } from '~src/types/db';
import { formatBytes } from '~src/utils/formatBytes';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useFolderProperties } from '~src/hooks/api-hooks/useFolderProperties';
import { getPath } from '~src/utils/getFilePath';
import { formatDate } from '~src/utils/formatDate';

const offsetValue = 1;
const labelSpan = 8;

interface FolderPropertiesModalProps extends Partial<ModalProps> {
  folder?: DbFolder;
}
export const FolderPropertiesModal: React.FC<FolderPropertiesModalProps> = ({
  onCancel,
  folder,
  ...modalProps
}) => {
  const style = useStyles(styleFn);
  const intl = useIntl();

  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );
  const folderProperties = useFolderProperties(
    {
      folderId: folder ? String(folder.id) : '',
    },
    // will refetch on modal show, if stale
    {
      enabled: !!modalProps.visible,
      staleTime: 30 * 1000, //30 sec
    }
  );

  const ErrorWrapper = (
    <div>
      {intl.formatMessage({ id: 'generic-error' })}.{' '}
      {intl.formatMessage({ id: 'try-again' })}
    </div>
  );

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'folder-properties' })}
          icon={['far', 'info-circle']}
        />
      }
      centered
      footer={null}
      onCancel={onCancel}
      width={360}
      {...modalProps}>
      {folderProperties.isLoading ? (
        <Spin />
      ) : folderProperties.error ? (
        ErrorWrapper
      ) : (
        <div css={style.text}>
          <Row>
            <Col span={labelSpan} css={style.span}>
              {intl.formatMessage({ id: 'name' })}:{' '}
            </Col>
            <Col offset={offsetValue}>
              <b>{folderProperties.data?.name}</b>
            </Col>
          </Row>
          <Row>
            <Col span={labelSpan} css={style.span}>
              {intl.formatMessage({ id: 'path' })}:{' '}
            </Col>
            <Col offset={offsetValue}>
              <b>{folder && getPath(foldersQuery.data, folder?.parent_id)}</b>
            </Col>
          </Row>
          <Row>
            <Col span={labelSpan} css={style.span}>
              {' '}
              {intl.formatMessage({ id: 'modified-date' })}:{' '}
            </Col>
            <Col offset={offsetValue}>
              <b>
                {' '}
                {folderProperties.data &&
                  formatDate(folderProperties.data?.updated_at, 'long')}
              </b>
            </Col>
          </Row>
          <Row>
            <Col span={labelSpan} css={style.span}>
              {intl.formatMessage({ id: 'uploaded-date' })}:{' '}
            </Col>
            <Col offset={offsetValue}>
              {' '}
              <b>
                {folderProperties.data &&
                  formatDate(folderProperties.data?.created_at, 'long')}
              </b>
            </Col>
          </Row>
          <Row>
            <Col span={labelSpan} css={style.span}>
              {intl.formatMessage({ id: 'contains' })}:{' '}
            </Col>
            <Col offset={offsetValue}>
              <b>
                {folderProperties.data && folderProperties.data.item_count}{' '}
              </b>
            </Col>
          </Row>
          <Row>
            <Col span={labelSpan} css={style.span}>
              {intl.formatMessage({ id: 'size' })}:{' '}
            </Col>
            <Col offset={offsetValue}>
              {' '}
              {folderProperties.data && (
                <b>{formatBytes(folderProperties.data?.total_size)}</b>
              )}
            </Col>
          </Row>
        </div>
      )}
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {},
  text: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '10px',
  },
  div: {
    color: theme.colors.text.primary,
    padding: '2px 0px',
  },
  span: {
    textAlign: 'right',
  },
});
